import { PrismicRichText } from '@prismicio/react';
import { GetStaticPropsResult } from 'next';
import { RichText } from 'prismic-reactjs';
import { Layout } from '../../components';
import { Locale, PrivacyPolicyProps } from '../../types';
import getPrismicMenuByLocale from '../../utils/prismic/menu';
import getPrismicOptionsByLocale from '../../utils/prismic/options';
import { getPrismicSingleItem } from '../../utils/prismic/single-item';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { i18nConfig } from '../../next-i18next.config';
import { REVALIDATION_TIME } from '../../utils/constants';

const PrivacyPolicy = ({
  privacyPolicy,
  options,
  menu,
}: PrivacyPolicyProps): JSX.Element => {
  const { title, policy } = privacyPolicy.data;
  return (
    <Layout options={options} menu={menu}>
      <div className="px-4 mx-auto mt-8 text-white max-w-7xl sm:px-6 lg:px-8 font-body">
        <div className="">
          <h1 className="text-xl font-semibold">{RichText.asText(title)}</h1>

          <PrismicRichText
            field={policy}
            components={{
              heading2: ({ children, key }) => (
                <h2 className="mt-2 text-lg font-semibold" key={key}>
                  {children}
                </h2>
              ),
              paragraph: ({ children, key }) => (
                <p className="mt-2" key={key}>
                  {children}
                </p>
              ),
              list: ({ children, key }) => (
                <ul className="mt-2 ml-8 list-disc" key={key}>
                  {children}
                </ul>
              ),

              hyperlink: ({ key, children, node: { data } }) => {
                return (
                  <a className="text-orange-600" key={key} href={data.url}>
                    {children}
                  </a>
                );
              },
            }}
          />
        </div>
      </div>
    </Layout>
  );
};

export const getStaticProps = async ({
  previewData,
  locale,
}: {
  previewData: any;
  locale: Locale;
}): Promise<GetStaticPropsResult<PrivacyPolicyProps>> => {
  const privacyPolicy = await getPrismicSingleItem('privacy-policy', locale);
  const options = await getPrismicOptionsByLocale(locale, previewData);
  const menu = await getPrismicMenuByLocale(locale, previewData);

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'], i18nConfig)),
      privacyPolicy,
      menu,
      options,
    },
    revalidate: REVALIDATION_TIME,
  };
};

export default PrivacyPolicy;
